import { Injectable, Inject } from '@angular/core';
import { core } from '@icc/common/helpers';
import { Common } from '@icc/common/Common';
import { WindowActiveConfiguration } from '@icc/common/configurations/WindowActiveConfiguration';
import { EventBusService } from '@icc/common/event-bus.service';
import { IssuesService, IssueLevel } from '@icc/helpers';
import { ValidationService } from '@icc/common/configurators/validation.service';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/common/config';;
import { SideProfile, SideProfileV2 } from '@icc/window';
import { CoupledWindowActiveConfiguration } from '@icc/common/configurations/CoupledWindowActiveConfiguration';
import { WindowConfiguration } from '@icc/common/configurations/WindowConfiguration';
import { BrowserProfilesService } from 'libs/configurator/window/src/lib/profiles/profiles.service';
import { TranslateService } from '@icc/common';

@Injectable()
export class ExtensionsService {
    constructor(
        private eventBusService: EventBusService,
        private profilesService: BrowserProfilesService,
        private issuesService: IssuesService,
        private validationService: ValidationService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private translateService: TranslateService
    ) {
        this.eventBusService.subscribe(
            ['setDefaultColors', 'setSystem', 'putExtensionOnSide', 'setExtensionProfile', 'setFrameProfile', 'changedDoorSizes'],
            data => {
                this.validateExtensionsAndFixIsses(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.value
                );
            }
        );
    }

    getFoundationExtensions(conf: WindowActiveConfiguration) {
        return this.profilesService.getFilteredProfiles(conf, 'sandwich');
    }

    setFoundationProfile(conf: WindowActiveConfiguration, foundation: {
        isFoundationProfile: boolean;
        foundationProfile: null | number;
        foundationProfileHeight: number;
    }) {
        if (foundation.isFoundationProfile) {
            conf.foundationProfile = true;
            const existingProfile = this.getFoundationProfile(conf);
            if (existingProfile) {
                existingProfile.width = existingProfile.widthOut = foundation.foundationProfileHeight;
                if (foundation.foundationProfile){
                    existingProfile.profileId = foundation.foundationProfile;
                }
            } else {
                const profile = this.profilesService.getProfile(foundation.foundationProfile);
                profile.width = foundation.foundationProfileHeight;
                this.profilesService.setProfileDefaultColors(profile, null, conf.Colors.frame, conf.Wood, conf.System);
                this.putExtensionOnSide(conf, profile, 'bottom');
            }
        } else {
            conf.foundationProfile = false;
            this.removeExtensionFromSide(conf, 'bottom');
        }
    }

    getFoundationProfile(conf: WindowActiveConfiguration) {
        const sides = this.profilesService.getFrameSidesOnEdge(conf);
        const windowSide = sides.find(s => s.side === 'bottom');
        const sideProfileOnSide = this.getLastSideProfileByFrameIndex(windowSide.frameEdges, conf);
        return sideProfileOnSide;
    }

    putExtensionOnSide(conf: WindowActiveConfiguration, profile, side) {
        if (profile.type === 'sandwich') {
            profile.widthOut = profile.width;
        }
        const sides = this.profilesService.getFrameSidesOnEdge(conf);
        const windowSide = sides.find(s => s.side === side);
        const sideProfileOnSide = this.getLastSideProfileByFrameIndex(windowSide.frameEdges, conf);

        const newSideProfile = new SideProfile({
            id: this.getIdForNew(conf),
            profileId: profile.id,
            length: 0,
            width: profile.width,
            widthOut: profile.widthOut,
            count: this.config().IccConfig.Configurators.hs.doubleExtensions && conf.System.confType === 'hs' ? 2 : 1,
            color: profile.selectedColor ? core.copy(profile.selectedColor.frame) : {},
            wood: profile.selectedWood ? core.copy(profile.selectedWood) : {},
            adjacentSideProfileId: sideProfileOnSide ? [sideProfileOnSide.id] : [],
            reinforcement: profile.reinforcement,
            framesId: !sideProfileOnSide
                ? windowSide.frameEdges.map(fE => ({
                      id: fE.frameId,
                      edges: [fE.frameEdgeIndex],
                  }))
                : [],
            side,
        });
        conf.SideProfiles.push(newSideProfile);
        if (sideProfileOnSide) {
            sideProfileOnSide.adjacentOtherSideProfileId.push(newSideProfile.id);
        }

        this.eventBusService.post({
            key: 'putExtensionOnSide',
            value: {
                profile,
                side,
            },
        });
        if (profile.type === 'sandwich') {
            profile.width = null;
            profile.widthOut = null;
        }
    }

    removeExtensionFromSide(conf: WindowActiveConfiguration, side) {
        const sides = this.profilesService.getFrameSidesOnEdge(conf);
        const windowSide = sides.find(s => s.side === side);
        const sideProfileOnSide = this.getLastSideProfileByFrameIndex(windowSide.frameEdges, conf);
        if (sideProfileOnSide) {
            conf.SideProfiles.splice(conf.SideProfiles.indexOf(sideProfileOnSide as SideProfile), 1);
            conf.SideProfiles.forEach(sp => {
                sp.adjacentOtherSideProfileId = sp.adjacentOtherSideProfileId.filter(
                    spId => spId !== sideProfileOnSide.id
                );
                sp.adjacentSideProfileId = sp.adjacentSideProfileId.filter(
                    spId => spId !== sideProfileOnSide.id
                );
            });

            this.eventBusService.post({
                key: 'removedExtensionFromSide',
                value: {
                    side,
                },
            });
            return true;
        } else {
            return false;
        }
    }

    removeAllExtensionsFromSide(conf: WindowActiveConfiguration, side) {
        while(this.removeExtensionFromSide(conf, side));
    }

    removeAllExtensions(conf: WindowActiveConfiguration) {
        this.removeAllExtensionsFromSide(conf, 'top');
        this.removeAllExtensionsFromSide(conf, 'bottom');
        this.removeAllExtensionsFromSide(conf, 'left');
        this.removeAllExtensionsFromSide(conf, 'right');
    }

    setDefaultColors(conf: WindowActiveConfiguration, colors) {
        const allColors = [].concat(colors.windowColors);

        Object.keys(conf.SideProfiles).map(side => {
            conf.SideProfiles.map(extension => {
                if (
                    extension.color
                    && extension.color.inner
                    && extension.color.outer
                    && !allColors.some(
                        el =>
                            el.id === extension.color.inner.id
                            && el.RAL === extension.color.inner.RAL
                    )
                    && !allColors.some(
                        el =>
                            el.id === extension.color.outer.id
                            && el.RAL === extension.color.outer.RAL
                    )
                ) {
                    extension.color = {
                        inner: null,
                        outer: null,
                    };
                }
            });
        });

        Object.assign(conf, this.validationService.valid(conf, 'extensionsColors'));
    }

    validateExtensionsAndFixIsses(conf: WindowActiveConfiguration, data) {
        this.validationService.indeterminate(conf, 'extensionsSystems');
        if (conf.SideProfiles) {
            if (this.isDifferentSashAndFrameShortening(conf)) {
                this.removeAllExtensionsFromSide(conf, 'bottom');
            }
            if (!this.validSystemsAndConnections(conf)) {
                this.validationService.invalid(conf, 'extensionsSystems');
                this.issuesService.simpleRegister(
                    'invalid-extensions',
                    'Niepoprawne profile poszerzeń',
                    this.translateService.instant('WINDOW|Niepoprawne profile poszerzeń'),
                    conf,
                    {
                        level: IssueLevel.ERROR,
                        logLevel: IssueLevel.NONE,
                        blockStepsAfter: null,
                    }
                );
                if (this.config().IccConfig.Configurators.removeExtensionsIfInvalid) {
                    this.removeAllExtensions(conf);
                }
            } else {
                Object.assign(conf, this.validationService.valid(conf, 'extensionsSystems'));
                this.issuesService.unregister('invalid-extensions', conf);
            }
        }
        if (Common.isDefined(data.windowColors)) {
            this.validationService.indeterminate(conf, 'extensionsColors');
            if (conf.SideProfiles) {
                if (!this.validColors(conf, data)) {
                    this.validationService.invalid(conf, 'extensionsColors');
                    this.setDefaultColors(conf, data);
                } else {
                    Object.assign(conf, this.validationService.valid(conf, 'extensionsColors'));
                }
            }
        }
    }

    validSystemsAndConnections(conf: WindowActiveConfiguration) {
        return Object.keys(conf.SideProfiles).every(side =>
            conf.SideProfiles.every(extension => {
                const profile = conf.UsedProfiles.find(el => el.id === extension.profileId);
                return (
                    profile && profile.systems && profile.systems.includes(Number(conf.System.id))
                    && this.profilesService.filterProfileByConnection(profile, conf)
                );
            })
        );
    }

    validColors(conf: WindowActiveConfiguration, colors) {
        const allColors = [].concat(colors.windowColors);

        Object.keys(conf.SideProfiles).map(side => {
            conf.SideProfiles.map(extension => {
                if (
                    extension.color
                    && extension.color.inner
                    && extension.color.outer
                    && !allColors.some(
                        el =>
                            el.id === extension.color.inner.id
                            && el.RAL === extension.color.inner.RAL
                    )
                    && !allColors.some(
                        el =>
                            el.id === extension.color.outer.id
                            && el.RAL === extension.color.outer.RAL
                    )
                ) {
                    return false;
                }
            });
        });

        return true;
    }

    getFirstSideProfileById(sideProfileId: number, conf: CoupledWindowActiveConfiguration) {
        const lastProfiles = conf.sideProfiles.filter(s => s.id === sideProfileId);
        const profiles = lastProfiles
            .map(s =>
                s.adjacentSideProfileId.length > 0 ? this.getBackProfiles(s, conf.sideProfiles) : s
            )
            .reduce<SideProfile[]>((acc, val) => acc.concat(val), []);
        return profiles[0];
    }

    getSideProfilesWidthById(sideProfileId: number, conf: CoupledWindowActiveConfiguration) {
        const lastProfiles = conf.sideProfiles.filter(s => s.id === sideProfileId);
        const profilesWidth =
            lastProfiles
                .map(s =>
                    s.adjacentSideProfileId.length > 0
                        ? this.getBackProfiles(s, conf.sideProfiles)
                        : []
                )
                .reduce<number>((acc, val) => acc + val.reduce((p, c) => p + c.width, 0), 0)
            + lastProfiles.reduce((p, c) => p + c.width, 0);
        return profilesWidth;
    }

    getIdForNew(conf: WindowActiveConfiguration | CoupledWindowActiveConfiguration) {
        const sideProfiles = CoupledWindowActiveConfiguration.is(conf)
            ? conf.sideProfiles
            : conf.SideProfiles;
        const intProfilesId = Object.keys(sideProfiles).reduce((prev, side) => {
            const maxId = sideProfiles.reduce(
                (intPrev, profile) => (profile.id > intPrev ? profile.id : intPrev),
                0
            );
            return maxId > prev ? maxId : prev;
        }, -1);
        return intProfilesId + 1;
    }

    getLastSideProfileByFrameIndex<
        T extends WindowActiveConfiguration | CoupledWindowActiveConfiguration | WindowConfiguration,
        S extends T extends WindowConfiguration ? SideProfileV2 : SideProfile
    >(
        frameEdges: {
            frameId: number;
            frameEdgeIndex: number;
            positionId?: string;
        }[],
        conf: WindowActiveConfiguration | CoupledWindowActiveConfiguration | WindowConfiguration
    ) {
        const firstProfiles = ((conf.type === 'coupled_window' || WindowConfiguration.is(conf)
            ? conf.sideProfiles
            : conf.SideProfiles
        ) as S[]).filter(s =>
            s.framesId.some(f =>
                frameEdges.some(
                    fE =>
                        f.id === fE.frameId
                        && f.edges.includes(fE.frameEdgeIndex)
                        && f.positionId === fE.positionId
                )
            )
        );
        const profiles = firstProfiles
            .map((s: S) =>
                s.adjacentOtherSideProfileId.length > 0
                    ? this.getNextProfiles(
                          s,
                          ((conf.type === 'coupled_window' || WindowConfiguration.is(conf)
                              ? conf.sideProfiles
                              : conf.SideProfiles) as S[])
                      )
                    : s
            )
            .reduce<S[]>((acc, val) => acc.concat(val), []);
        return profiles[0];
    }

    private getBackProfiles<
        T extends SideProfile | SideProfileV2,
        S extends T extends SideProfile ? SideProfile : SideProfileV2
    >(baseProfile: S, sideProfiles: S[]): S[] {
        const backProfiles = sideProfiles.filter(s =>
            baseProfile.adjacentSideProfileId.includes(s.id)
        );
        const allBackProfiles = backProfiles
            .map(s =>
                s.adjacentSideProfileId.length > 0 ? this.getBackProfiles(s, sideProfiles) : s
            )
            .reduce<S[]>((acc, val) => acc.concat(val), []);
        return allBackProfiles;
    }

    private getNextProfiles<
        T extends SideProfile | SideProfileV2,
        S extends T extends SideProfile ? SideProfile : SideProfileV2
    >(baseProfile: S, sideProfiles: S[]): S[] {
        const nextProfiles = sideProfiles.filter(s =>
            baseProfile.adjacentOtherSideProfileId.includes(s.id)
        );
        const allNextProfiles = nextProfiles
            .map(s =>
                s.adjacentOtherSideProfileId.length > 0 ? this.getNextProfiles(s, sideProfiles) : s
            )
            .reduce<S[]>((acc, val) => acc.concat(val), []);
        return allNextProfiles;
    }

    isDifferentSashAndFrameShortening(conf: WindowActiveConfiguration): boolean {
        return conf.type === 'door'
                && conf.System.seperate_frame_and_sash_shortening
                && conf.doorSizes
                && conf.doorSizes.frameShortening != conf.doorSizes.shortening
    }
}
